import React, { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingFlatRoundedIcon from "@mui/icons-material/TrendingFlatRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import { formatUtcDateSimple } from "../../../helpers/formatUtcDate";
import { AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK } from "../AuditHub/theme";
import {
  CardHeader,
  CardShell,
  FAILED_TEXT_COLOR,
  PASSED_TEXT_COLOR,
  ResourcesTileBody,
  ScoreTileBody,
  Tile,
  bigNumberSx,
  tileGridSx,
  wideTileSx,
} from "./AuditCardParts";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Filler, ChartTooltip);

const TREND_COLOR = "#1976d2";
const TREND_DAYS = 30;
const CHART_HEIGHT = 64;

// Trend keys and scan times are both UTC, so the day is read in UTC rather than the browser's zone.
const toDayKey = (value) => {
  const leading = /^\d{4}-\d{2}-\d{2}/.exec(String(value));
  if (leading) return leading[0];
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

const formatDay = (dayKey) => {
  const [year, month, day] = dayKey.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? dayKey : date.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
};

const DeltaBadge = ({ delta }) => {
  if (delta == null) return null;
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const tone = {
    up: { color: PASSED_TEXT_COLOR, bg: "#f0fdf4", Icon: TrendingUpRoundedIcon, text: `+${delta.toFixed(2)}%` },
    down: { color: FAILED_TEXT_COLOR, bg: "#fef2f2", Icon: TrendingDownRoundedIcon, text: `${delta.toFixed(2)}%` },
    flat: { color: AH_MUTED, bg: AH_TRACK, Icon: TrendingFlatRoundedIcon, text: "No change" },
  }[direction];
  return (
    <Tooltip title="Change since the previous scan" arrow placement="top">
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: 99,
          bgcolor: tone.bg,
          color: tone.color,
          flexShrink: 0,
        }}
      >
        <tone.Icon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "inherit", whiteSpace: "nowrap" }}>{tone.text}</Typography>
      </Box>
    </Tooltip>
  );
};

/** Audit view of one account on one framework: score, controls, resources and the 30-day trend. */
const AuditAccountCard = ({ sections, compliance, scanDate, compliancePercent, accountNumber, controlCounts, accountSwitcher }) => {
  const score = compliancePercent == null || compliancePercent === "" ? NaN : Number(compliancePercent);

  const controls = useMemo(() => {
    if (controlCounts) return controlCounts;
    const list = Array.isArray(sections?.controlsListJson) ? sections.controlsListJson : [];
    return list.reduce(
      (acc, category) => ({
        passed: acc.passed + (Number(category.PassedControls) || 0),
        failed: acc.failed + (Number(category.FailedControls) || 0),
      }),
      { passed: 0, failed: 0 }
    );
  }, [sections, controlCounts]);

  // Trend points up to this scan's day, so an older run is never compared with later scans.
  const trend = useMemo(() => {
    const scanDay = scanDate ? toDayKey(scanDate) : null;
    const points = Object.entries(sections?.trendScore || {})
      .filter(([day, value]) => typeof value === "number" && (!scanDay || day.slice(0, 10) <= scanDay))
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .slice(-TREND_DAYS);
    const delta = points.length > 1 ? points[points.length - 1][1] - points[points.length - 2][1] : null;
    return { labels: points.map(([day]) => formatDay(day.slice(0, 10))), values: points.map(([, value]) => value), delta };
  }, [sections, scanDate]);

  const chartData = useMemo(
    () => ({
      labels: trend.labels,
      datasets: [
        {
          data: trend.values,
          fill: true,
          borderColor: TREND_COLOR,
          backgroundColor: "rgba(25, 118, 210, 0.10)",
          pointBackgroundColor: TREND_COLOR,
          pointRadius: 2,
          pointHoverRadius: 4,
          tension: 0.35,
          borderWidth: 2,
        },
      ],
    }),
    [trend]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 6, left: 4, right: 4 } },
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#fff",
          titleColor: AH_TEXT,
          bodyColor: TREND_COLOR,
          borderColor: AH_BORDER,
          borderWidth: 1,
          padding: 10,
          displayColors: false,
          titleFont: { weight: "700" },
          bodyFont: { weight: "700" },
          callbacks: { label: (context) => `Compliance score: ${Number(context.parsed.y).toFixed(2)}%` },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: AH_MUTED, font: { size: 10, weight: 500 }, maxTicksLimit: 6, maxRotation: 0, autoSkip: true },
        },
        y: { display: false, min: 0, max: 100 },
      },
    }),
    []
  );

  return (
    <CardShell>
      <CardHeader
        eyebrow="Account Overview"
        compliance={compliance}
        meta={
          scanDate ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarMonthIcon sx={{ color: AH_MUTED, fontSize: 14 }} />
              <span>Scan date: {formatUtcDateSimple(scanDate)}</span>
            </Box>
          ) : null
        }
        action={accountSwitcher || <Typography sx={{ color: AH_MUTED, fontSize: 13 }}>Account: {accountNumber}</Typography>}
      />

      <Box sx={tileGridSx}>
        <Tile label="Compliance Score">
          <ScoreTileBody score={score} passed={controls.passed} failed={controls.failed} />
        </Tile>

        <Tile label="Resources Scanned">
          <ResourcesTileBody
            total={Number(sections?.totalResources) || 0}
            passed={Number(sections?.passedResources) || 0}
            failed={Number(sections?.failedResources) || 0}
          />
        </Tile>

        <Tile label={`Compliance Trend · Last ${TREND_DAYS} Days`} action={<DeltaBadge delta={trend.delta} />} sx={wideTileSx}>
          {/* The chart needs a fixed-height parent: a responsive canvas in a stretching flex box keeps growing. */}
          {trend.values.length > 1 ? (
            <Box sx={{ position: "relative", height: CHART_HEIGHT, width: "100%" }}>
              <Line data={chartData} options={chartOptions} aria-label="Compliance score trend" role="img" />
            </Box>
          ) : trend.values.length === 1 ? (
            <>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={bigNumberSx}>{trend.values[0].toFixed(2)}%</Typography>
                <Typography sx={{ fontSize: 13, color: AH_MUTED }}>on {trend.labels[0]}</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: AH_MUTED }}>The trend line appears after the next scan.</Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 13, color: AH_MUTED }}>No trend data yet</Typography>
          )}
        </Tile>
      </Box>
    </CardShell>
  );
};

export default AuditAccountCard;
